/**
 * The goal: a target weight and/or body fat %, reached by a date or at a pace, and the
 * recommended daily intake that gets there (worked out, not typed in).
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { weightAtBodyFatKeepingLean } from '@engine/trajectory';
import { formatDayLong } from '../../lib/dates';
import { errorMessage } from '../../lib/logger';
import { planGoal, PACE_OPTIONS, type GoalPlan } from '../../lib/goalPlan';
import { planFromTargets, planMacros, withCalories } from '../../lib/macros';
import type { BodyGoal } from '../../lib/types';
import { massFromKg, massToKg, massUnit } from '../../lib/units';
import { useApp, useAsync } from '../../state/app';
import { Icon } from '../Icon';
import { Card, Segmented, Spinner } from '../ui';

type Timing = 'date' | 'pace';
const numOrNull = (s: string): number | null => { const t = s.trim(); if (t === '') return null; const n = Number(t); return Number.isFinite(n) ? n : null; };
const fmt = (n: number): string => Math.round(n).toLocaleString();

export function GoalCard({ onPlan }: { onPlan: (kcal: number | null) => void }): ReactElement {
  const { source, profile, setProfile, settings, saveSettings, energy, today, toast, bump, version } = useApp();
  const units = profile.unit_system;
  const goalQ = useAsync<BodyGoal | null>(() => source.getActiveGoal(), [source, version]);
  const [weight, setWeight] = useState<string>('');
  const [bodyFat, setBodyFat] = useState<string>('');
  const [timing, setTiming] = useState<Timing>('pace');
  const [date, setDate] = useState<string>('');
  const [pace, setPace] = useState<string>(settings.goal_pace_pct !== null ? String(settings.goal_pace_pct) : '');
  const [saving, setSaving] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const g: BodyGoal | null = goalQ.data;
    if (!g) return;
    setWeight(g.target_weight_kg !== null ? String(Math.round(massFromKg(g.target_weight_kg, units) * 10) / 10) : '');
    setBodyFat(g.target_body_fat_pct !== null ? String(g.target_body_fat_pct) : '');
    setDate(g.target_date ?? '');
    setTiming(g.target_date ? 'date' : 'pace');
  }, [goalQ.data, units]);
  useEffect(() => { if (settings.goal_pace_pct !== null) setPace(String(settings.goal_pace_pct)); }, [settings.goal_pace_pct]);

  const c = energy.comp;
  const targetKg: number | null = (() => { const w = numOrNull(weight); return w === null ? null : massToKg(w, units); })();
  const targetBf: number | null = numOrNull(bodyFat);
  const paceNum: number | null = numOrNull(pace);

  const plan: GoalPlan = useMemo(() => planGoal({
    today, weightKg: c.weightKg, bodyFatPct: c.bodyFatPct, leanMassKg: c.leanMeasured ? c.leanMassKg : null,
    targetWeightKg: targetKg, targetBodyFatPct: targetBf, targetDate: timing === 'date' && date ? date : null,
    pacePct: timing === 'pace' ? paceNum : null, tdeeKcal: energy.planning.kcal, bmrKcal: energy.bmr?.kcal ?? null,
  }), [today, c.weightKg, c.bodyFatPct, c.leanMassKg, c.leanMeasured, targetKg, targetBf, timing, date, paceNum, energy.planning.kcal, energy.bmr]);

  const recommended: number | null = plan.ok && plan.dailyCalories >= 800 && plan.dailyCalories <= 10000 ? plan.dailyCalories : null;
  useEffect(() => { onPlan(recommended); }, [recommended, onPlan]);

  const losing: boolean | null = targetKg !== null && c.weightKg !== null ? targetKg < c.weightKg
    : targetBf !== null && c.bodyFatPct !== null ? targetBf < c.bodyFatPct : null;
  const paceOptions = PACE_OPTIONS.filter((o) => (losing === null ? true : losing ? o.pct < 0 : o.pct > 0));
  const leanTarget: number | null = targetBf !== null && c.weightKg !== null && c.bodyFatPct !== null && targetBf > 0 && targetBf < 75
    ? weightAtBodyFatKeepingLean(c.weightKg, c.bodyFatPct, targetBf) : null;

  const saveGoal = async (): Promise<void> => {
    setError(null);
    if (targetKg === null && targetBf === null) { setError('Set a target weight or a target body fat %.'); return; }
    if (targetKg !== null && (targetKg < 20 || targetKg > 400)) { setError('That target weight is out of range.'); return; }
    if (targetBf !== null && (targetBf < 2 || targetBf > 75)) { setError('Target body fat must be between 2 and 75%.'); return; }
    if (timing === 'date' && (date === '' || date <= today)) { setError('Pick a target date in the future, or plan by pace.'); return; }
    setSaving(true);
    try {
      await source.saveGoal({
        target_weight_kg: targetKg === null ? null : Math.round(targetKg * 100) / 100,
        target_lean_mass_kg: null,
        target_body_fat_pct: targetBf === null ? null : Math.round(targetBf * 10) / 10,
        target_date: timing === 'date' ? date : null,
        planned_daily_calories: recommended,
        notes: null,
      });
      await saveSettings({ goal_pace_pct: timing === 'pace' ? (paceNum ?? (losing === false ? 0.25 : -0.5)) : null });
      toast('Goal saved');
      bump();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const applyToTargets = async (): Promise<void> => {
    if (recommended === null) return;
    setApplying(true);
    setError(null);
    try {
      const base = settings.macro_plan ?? planFromTargets(profile);
      const current = profile.target_protein_g !== null && profile.target_fat_g !== null ? { protein_g: profile.target_protein_g, fat_g: profile.target_fat_g } : null;
      const next = withCalories(base, recommended, current);
      const r = planMacros(next, { weightKg: c.weightKg, leanMassKg: c.leanMassKg });
      if (!r.ok) { setError(`Couldn’t fit your macros into ${fmt(recommended)} kcal: ${r.error}`); return; }
      setProfile(await source.updateProfile({ target_calories: r.targets.calories, target_protein_g: r.targets.protein_g, target_carbs_g: r.targets.carbs_g, target_fat_g: r.targets.fat_g }));
      await saveSettings({ macro_plan: next });
      toast(`Daily target set to ${r.targets.calories.toLocaleString()} kcal`);
      bump();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  const u: string = massUnit(units);
  const burnText: string = energy.planning.method === 'wearable' ? `your watch average (${energy.planning.days} days)`
    : energy.planning.method === 'observed' ? 'your learned burn' : energy.planning.method === 'activity' ? 'BMR × activity level' : '';

  return (
    <Card title="Goal" sub="one active at a time">
      <div className="stack" style={{ gap: 14 }}>
        <div className="faint" style={{ fontSize: 13, fontWeight: 700 }}>
          Now: {c.weightKg !== null ? `${massFromKg(c.weightKg, units).toFixed(1)} ${u}` : 'no weigh-in yet'}{c.bodyFatPct !== null ? ` · ${c.bodyFatPct.toFixed(1)}% body fat` : ' · body fat not set'}
        </div>
        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <label className="field"><span>Target weight</span><div className="input-suffix"><input className="input" type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} /><em>{u}</em></div></label>
          <label className="field"><span>Target body fat</span><div className="input-suffix"><input className="input" type="number" step="0.1" value={bodyFat} onChange={(e) => setBodyFat(e.target.value)} /><em>%</em></div>
            {leanTarget !== null && <small>≈ {massFromKg(leanTarget, units).toFixed(1)} {u} if you keep all your lean mass{targetKg !== null ? ' (your target weight is used for the plan)' : ''}</small>}
          </label>
        </div>
        <div className="field"><span>How to get there</span>
          <Segmented<Timing> options={[{ value: 'pace', label: 'At a steady pace' }, { value: 'date', label: 'By a date' }]} value={timing} onChange={setTiming} />
        </div>
        {timing === 'date' ? (
          <label className="field"><span>Target date</span><input className="input" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} /></label>
        ) : (
          <label className="field"><span>Pace</span>
            <select className="input" value={pace} onChange={(e) => setPace(e.target.value)}>
              <option value="">{losing === false ? 'Lean gain: 0.25% a week (recommended)' : 'Steady: lose 0.5% a week (recommended)'}</option>
              {paceOptions.map((o) => <option key={o.pct} value={String(o.pct)}>{o.label}</option>)}
            </select>
          </label>
        )}

        <div className="goal-reco">
          <div className="goal-reco-label">Recommended intake for your goal</div>
          {plan.ok ? (
            <>
              <div className="goal-reco-value num">{fmt(plan.dailyCalories)} <small>kcal a day</small></div>
              <div className="muted" style={{ fontSize: 13.5, fontWeight: 600 }}>
                {plan.basis === 'maintain' ? 'Your typical burn: this keeps your weight steady.' : <>
                  {fmt(Math.abs(plan.vsBurnKcal))} kcal {plan.vsBurnKcal < 0 ? 'below' : 'above'} your typical burn of {fmt(energy.planning.kcal ?? 0)} ({burnText}).{' '}
                  About {massFromKg(Math.abs(plan.weeklyChangeKg), units).toFixed(2)} {u} a week ({Math.abs(plan.weeklyChangePct).toFixed(2)}% of body weight).
                  {plan.reachDate && <> Reaches {massFromKg(plan.targetWeightKg, units).toFixed(1)} {u}{plan.endBodyFatPct !== null ? ` (${plan.endBodyFatPct.toFixed(1)}% body fat)` : ''} around <b>{formatDayLong(plan.reachDate)}</b>.</>}
                </>}
              </div>
              {plan.warnings.map((w) => <div key={w} className="note warn" style={{ marginTop: 6 }}>{w}</div>)}
              <div className="row" style={{ marginTop: 10 }}>
                <button type="button" className="btn btn-sm" onClick={() => void applyToTargets()} disabled={applying || recommended === null || recommended === profile.target_calories}>
                  {applying ? <Spinner /> : <Icon name="check" size={15} />} {recommended === profile.target_calories ? 'This is your calorie target' : `Use ${fmt(plan.dailyCalories)} kcal as my daily target`}
                </button>
              </div>
            </>
          ) : (
            <div className="muted" style={{ fontSize: 13.5, fontWeight: 600 }}>{plan.reason}</div>
          )}
        </div>
        <p className="cite" style={{ margin: 0 }}>
          Uses the same model as Trends: the energy in a pound of weight change depends on your body fat (Hall 2008), some lean mass goes with the fat (Forbes), and your burn drops as your weight drops (Hall 2011). Suggested paces: losing 0.5-1% of body weight a week keeps more muscle (Helms 2014; Garthe 2011); gaining 0.25-0.5% a week limits fat gain (Iraki 2019).
        </p>
        {error !== null && <div className="note err">{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={() => void saveGoal()} disabled={saving}>{saving ? <Spinner /> : <Icon name="target" size={18} />} Save goal</button>
        </div>
      </div>
    </Card>
  );
}
